import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { ISession } from '../../../models/pro-sites';
import { IOrderDetails } from '../../../models/product-detail';
import { addToCart } from '../../../store/product/product-detail-actions';
import Gallery from '../../common/gallery';
import WishListItemElement from './wish-list-item-element';
import { gaEvent } from '../../../hooks/ga-hook';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux-hooks';
import { loadWishlists } from '../../../store/e-store/wish-list-slice';
import { toast } from 'react-toastify';

interface IWishListGalleryProps {
    session: ISession;
    addToCart: (order: IOrderDetails) => Promise<boolean>;
    selectedAgreement: string;
}

const responsive = {
    0: { items: 1 },
    1024: { items: 3 },
};

export const WishListItemGallery = ({
    session,
    addToCart,
    selectedAgreement,
}: IWishListGalleryProps) => {
    const intl = useIntl();
    const maxQuantity = session!.settings!.MaxQuantity;
    const country = session!.country;
    const dispatch = useAppDispatch();

    const favorites = useAppSelector(
        (state: RootState) => state.wishlists.favorites
    );
    const recentlyViewedProducts = useAppSelector(
        (state: RootState) => state.wishlists.recentlyViewedProducts
    );

    useEffect(() => {
        dispatch(loadWishlists());
    }, [dispatch]);

    const handleAddFavoriteToCart = (orderDetails: IOrderDetails) => {
        gaEvent('Favorites', 'Add to Cart', orderDetails.partNumber);
        return addToCart(orderDetails);
    };

    const handleRecentlyViewedAddToCart = (orderDetails: IOrderDetails) => {
        gaEvent('Recently Viewed', 'Add to Cart', orderDetails.partNumber);
        return addToCart(orderDetails);
    };
    const handleAgreementRequired = (e: any) => {
        if (selectedAgreement.length === 0 && session.country === 'CA') {
            e.preventDefault();
            toast.error(
                'Please select a Benefit to navigate to the Product Details Page',
                {
                    position: 'bottom-right',
                    autoClose: 10000,
                }
            );
            return;
        }
    };
    const handleOnNavigateFavorite = (e?: any) => {
        handleAgreementRequired(e);
        gaEvent('Favorites', 'Navigate to Product Detail', '');
    };

    const handleOnNavigateRecentlyViewed = (e?: any) => {
        handleAgreementRequired(e);
        gaEvent('Recently Viewed', 'Navigate to Product Detail', '');
    };

    return (
        <>
            {favorites.length > 0 && (
                <Gallery
                    title={intl.formatMessage({
                        id: 'Favorites',
                        defaultMessage: 'Favorites',
                    })}
                    titleIcon="far fa-star"
                    marginBottom={60}
                    responsive={responsive}
                    items={favorites.map((item, index) => {
                        return (
                            <WishListItemElement
                                key={index}
                                title={item.shortDescription}
                                subTitle={item.defaultProductNumber}
                                imageUrl={item.smallImagePath}
                                imageAltText={item.defaultProductNumber}
                                callToActionUrl={
                                    '/estore/product-detail/' +
                                    item.productId +
                                    '?agreementName=' +
                                    encodeURIComponent(selectedAgreement)
                                }
                                handleOnNavigate={handleOnNavigateFavorite}
                                productId={item.productId}
                                cartItemTypeId={item.cartItemTypeId}
                                partNumber={item.erpNumber}
                                maxQuantity={maxQuantity}
                                addToCart={handleAddFavoriteToCart}
                                selectedAgreement={selectedAgreement}
                                country={country}
                            />
                        );
                    })}
                />
            )}
            {recentlyViewedProducts.length > 0 && (
                <Gallery
                    title={intl.formatMessage({
                        id: 'Recently_Viewed',
                        defaultMessage: 'Recently Viewed',
                    })}
                    titleIcon="far fa-clock"
                    marginTop={60}
                    responsive={responsive}
                    items={recentlyViewedProducts.map((item, index) => {
                        return (
                            <WishListItemElement
                                key={index}
                                title={item.shortDescription}
                                subTitle={item.defaultProductNumber}
                                imageUrl={item.smallImagePath}
                                imageAltText={item.defaultProductNumber}
                                callToActionUrl={
                                    '/estore/product-detail/' +
                                    item.productId +
                                    '?agreementName=' +
                                    encodeURIComponent(selectedAgreement)
                                }
                                handleOnNavigate={
                                    handleOnNavigateRecentlyViewed
                                }
                                productId={item.productId}
                                cartItemTypeId={item.cartItemTypeId}
                                partNumber={item.erpNumber}
                                maxQuantity={maxQuantity}
                                addToCart={handleRecentlyViewedAddToCart}
                                selectedAgreement={selectedAgreement}
                                country={country}
                            />
                        );
                    })}
                />
            )}
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    session: state.session!.data!,
});

const mapDispatchToProps = {
    addToCart,
};

const wishListGalleryContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(WishListItemGallery);

export default wishListGalleryContainer;
